import { fromCentsAsNumber } from '@payer-admin-ui/components-v2/src/utils/currency';
import { generateUUID } from '@payer-admin-ui/components-v2/src/utils/strings';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BankFilePayment, MappingType } from '@payer-admin-ui/api/src/bankfilepayments/bankFilePaymentTypes';
import type { BankfileMappingSuggestion } from '@payer-admin-ui/api/src/ai/types/BankfileMappingSuggestion';
import {
  AddMappingMode,
  DraftInvoiceMapping,
  DraftStatementMapping,
  initialState,
  InvoiceSearchResultItem,
  StatementSearchResultItem,
} from './AddMappingModalStateConstants';
import { toDraftInvoiceMappingsFromBankfileSuggestions } from './bankfileMappingSuggestionMapper';
import { invoiceFilterAdapter, statementFilterAdapter } from './addMappingModalFilterAdapters';
import {
  applyInvoiceDraftMappings,
  applyStatementDraftMappings,
  searchStatements,
  searchInvoices,
} from './AddMappingModalStateThunks';

const slice = createSlice({
  name: 'bankFilePayments/actions/AddMappingModal',
  initialState,
  reducers: {
    reset: () => ({ ...initialState }),
    setMode: (state, { payload }: PayloadAction<AddMappingMode>) => ({ ...state, mode: payload }),
    setRecommendedInvoiceDraftMappings: (
      state,
      {
        payload,
      }: PayloadAction<{
        mappings: BankfileMappingSuggestion[];
      }>,
    ) => {
      return {
        ...state,
        mode: 'INVOICE',
        invoice: {
          ...state.invoice,
          draftMappings: toDraftInvoiceMappingsFromBankfileSuggestions(payload.mappings),
        },
      };
    },
    setInvoiceQuery: (state, { payload }: PayloadAction<string>) => ({
      ...state,
      invoice: { ...state.invoice, search: { ...state.invoice.search, query: payload } },
    }),
    setStatementQuery: (state, { payload }: PayloadAction<string>) => ({
      ...state,
      statement: { ...state.statement, search: { ...state.statement.search, query: payload } },
    }),
    setInvoiceAmountError: (
      state,
      { payload: { draftId, message } }: PayloadAction<{ draftId: string; message?: string }>,
    ) => {
      if (!state.invoice.draftMappings[draftId]) return;
      state.invoice.draftMappings[draftId].error = message;
    },
    setStatementAmountError: (
      state,
      { payload: { draftId, message } }: PayloadAction<{ draftId: string; message?: string }>,
    ) => {
      if (!state.statement.draftMappings[draftId]) return;
      state.statement.draftMappings[draftId].error = message;
    },
    setInvoiceFilter: invoiceFilterAdapter.setFilter,
    clearAllInvoiceFilters: invoiceFilterAdapter.clearAllFilters,
    clearInvoiceFilter: invoiceFilterAdapter.clearFilter,
    applyInvoiceFilters: invoiceFilterAdapter.applyFilters,
    setStatementFilter: statementFilterAdapter.setFilter,
    clearAllStatementFilters: statementFilterAdapter.clearAllFilters,
    clearStatementFilter: statementFilterAdapter.clearFilter,
    applyStatementFilters: statementFilterAdapter.applyFilters,
    addGenericInvoiceDraftMapping: (
      state,
      { payload: { mappingType, amount } }: PayloadAction<{ mappingType: MappingType; amount: number }>,
    ) => {
      if (!amount || amount <= 0) return state;
      const draft: DraftInvoiceMapping = {
        id: generateUUID(),
        mappingType,
        amount,
        isConfirmed: false,
      };
      return {
        ...state,
        invoice: { ...state.invoice, draftMappings: { ...state.invoice.draftMappings, [draft.id]: draft } },
      };
    },
    addInvoiceDraftMapping: (
      state,
      {
        payload: { item, bankFilePayment },
      }: PayloadAction<{ item: InvoiceSearchResultItem; bankFilePayment: BankFilePayment }>,
    ) => {
      const patch: Record<string, DraftInvoiceMapping> = {};
      const totalDraftedAmount = Object.values(state.invoice.draftMappings).reduce(
        (sum, draft) => sum + (draft.amount || 0),
        0,
      );
      const baseRemainingUnmapped = fromCentsAsNumber(bankFilePayment.remainingUnmappedAmount);
      const remainingUnmappedAmount = Math.max(0, baseRemainingUnmapped - totalDraftedAmount);
      const normalMappingAmount = item.balance == null ? 0 : Math.min(Number(item.balance), remainingUnmappedAmount);
      const normalMapping: DraftInvoiceMapping = {
        id: generateUUID(),
        type: item.type,
        mappingType: 'NORMAL',
        number: item.number,
        amount: normalMappingAmount,
        isConfirmed: false,
      };
      patch[normalMapping.id] = normalMapping;
      const addFeeMapping =
        item.feeBalance != null && Number(item.feeBalance) > 0 && remainingUnmappedAmount - normalMappingAmount > 0;
      if (addFeeMapping) {
        const feeMappingAmount = Math.min(Number(item.feeBalance), remainingUnmappedAmount - normalMappingAmount);
        const feeMapping: DraftInvoiceMapping = {
          id: generateUUID(),
          type: item.type,
          mappingType: 'FEE',
          number: item.number,
          amount: feeMappingAmount,
          isConfirmed: false,
        };
        patch[feeMapping.id] = feeMapping;
      }
      return {
        ...state,
        invoice: { ...state.invoice, draftMappings: { ...state.invoice.draftMappings, ...patch } },
      };
    },
    setInvoiceDraftMappingAmount: (
      state,
      { payload: { draftId, amount } }: PayloadAction<{ draftId: string; amount: number }>,
    ) => ({
      ...state,
      invoice: {
        ...state.invoice,
        draftMappings: {
          ...state.invoice.draftMappings,
          [draftId]: { ...state.invoice.draftMappings[draftId], amount },
        },
      },
    }),
    setInvoiceDraftMappingMappingType: (
      state,
      { payload: { draftId, mappingType } }: PayloadAction<{ draftId: string; mappingType: MappingType }>,
    ) => ({
      ...state,
      invoice: {
        ...state.invoice,
        draftMappings: {
          ...state.invoice.draftMappings,
          [draftId]: { ...state.invoice.draftMappings[draftId], mappingType },
        },
      },
    }),
    confirmInvoiceDraftMapping: (state, { payload: { draftId } }: PayloadAction<{ draftId: string }>) => ({
      ...state,
      invoice: {
        ...state.invoice,
        draftMappings: {
          ...state.invoice.draftMappings,
          [draftId]: { ...state.invoice.draftMappings[draftId], isConfirmed: true },
        },
      },
    }),
    editInvoiceDraftMapping: (state, { payload: { draftId } }: PayloadAction<{ draftId: string }>) => ({
      ...state,
      invoice: {
        ...state.invoice,
        draftMappings: {
          ...state.invoice.draftMappings,
          [draftId]: { ...state.invoice.draftMappings[draftId], isConfirmed: false },
        },
      },
    }),
    removeInvoiceDraftMapping: (state, { payload: { draftId } }: PayloadAction<{ draftId: string }>) => {
      const { [draftId]: _, ...remainingMappings } = state.invoice.draftMappings;
      return {
        ...state,
        invoice: { ...state.invoice, draftMappings: remainingMappings },
      };
    },
    addStatementDraftMapping: (
      state,
      {
        payload: { item, bankFilePayment },
      }: PayloadAction<{ item: StatementSearchResultItem; bankFilePayment: BankFilePayment }>,
    ) => {
      const totalDraftedAmount = Object.values(state.statement.draftMappings).reduce(
        (sum, draft) => sum + (draft.amount || 0),
        0,
      );
      const baseRemainingUnmapped = fromCentsAsNumber(bankFilePayment.remainingUnmappedAmount);
      const remainingUnmappedAmount = Math.max(0, baseRemainingUnmapped - totalDraftedAmount);
      const mappingAmount =
        item.originalAmount == null ? 0 : Math.min(Number(item.originalAmount), remainingUnmappedAmount);
      const draft: DraftStatementMapping = {
        id: generateUUID(),
        number: item.number,
        amount: mappingAmount,
        isConfirmed: false,
      };
      return {
        ...state,
        statement: {
          ...state.statement,
          draftMappings: { ...state.statement.draftMappings, [draft.id]: draft },
        },
      };
    },
    setStatementDraftMappingAmount: (
      state,
      { payload: { draftId, amount } }: PayloadAction<{ draftId: string; amount: number }>,
    ) => ({
      ...state,
      statement: {
        ...state.statement,
        draftMappings: {
          ...state.statement.draftMappings,
          [draftId]: { ...state.statement.draftMappings[draftId], amount },
        },
      },
    }),
    confirmStatementDraftMapping: (state, { payload: { draftId } }: PayloadAction<{ draftId: string }>) => ({
      ...state,
      statement: {
        ...state.statement,
        draftMappings: {
          ...state.statement.draftMappings,
          [draftId]: { ...state.statement.draftMappings[draftId], isConfirmed: true },
        },
      },
    }),
    editStatementDraftMapping: (state, { payload: { draftId } }: PayloadAction<{ draftId: string }>) => ({
      ...state,
      statement: {
        ...state.statement,
        draftMappings: {
          ...state.statement.draftMappings,
          [draftId]: { ...state.statement.draftMappings[draftId], isConfirmed: false },
        },
      },
    }),
    removeStatementDraftMapping: (state, { payload: { draftId } }: PayloadAction<{ draftId: string }>) => {
      const { [draftId]: _, ...remainingMappings } = state.statement.draftMappings;
      return {
        ...state,
        statement: { ...state.statement, draftMappings: remainingMappings },
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(searchInvoices.pending, state => {
      state.invoice.search.isFetching = true;
      state.invoice.search.error = null;
    });
    builder.addCase(searchInvoices.fulfilled, (state, { payload }) => {
      state.invoice.search.isFetching = false;
      state.invoice.search.results = payload;
    });
    builder.addCase(searchInvoices.rejected, (state, { error }) => {
      state.invoice.search.isFetching = false;
      state.invoice.search.error = error;
    });

    builder.addCase(searchStatements.pending, state => {
      state.statement.search.isFetching = true;
      state.statement.search.error = null;
    });
    builder.addCase(searchStatements.fulfilled, (state, { payload }) => {
      state.statement.search.isFetching = false;
      state.statement.search.results = payload;
    });
    builder.addCase(searchStatements.rejected, (state, { error }) => {
      state.statement.search.isFetching = false;
      state.statement.search.error = error;
    });

    builder.addCase(applyInvoiceDraftMappings.pending, state => {
      state.isFetching = true;
      state.error = null;
      state.success = null;
    });
    builder.addCase(applyInvoiceDraftMappings.fulfilled, state => {
      state.isFetching = false;
      state.success = true;
    });
    builder.addCase(applyInvoiceDraftMappings.rejected, (state, { error }) => {
      state.isFetching = false;
      state.error = error;
      state.success = false;
    });

    builder.addCase(applyStatementDraftMappings.pending, state => {
      state.isFetching = true;
      state.error = null;
      state.success = null;
    });
    builder.addCase(applyStatementDraftMappings.fulfilled, state => {
      state.isFetching = false;
      state.success = true;
    });
    builder.addCase(applyStatementDraftMappings.rejected, (state, { error }) => {
      state.isFetching = false;
      state.error = error;
      state.success = false;
    });
  },
});

export const {
  actions: {
    reset,
    setMode,
    setRecommendedInvoiceDraftMappings,
    setInvoiceQuery,
    setStatementQuery,
    setInvoiceAmountError,
    setStatementAmountError,
    setInvoiceFilter,
    clearAllInvoiceFilters,
    clearInvoiceFilter,
    applyInvoiceFilters,
    setStatementFilter,
    clearAllStatementFilters,
    clearStatementFilter,
    applyStatementFilters,
    addInvoiceDraftMapping,
    addGenericInvoiceDraftMapping,
    setInvoiceDraftMappingAmount,
    setInvoiceDraftMappingMappingType,
    confirmInvoiceDraftMapping,
    editInvoiceDraftMapping,
    removeInvoiceDraftMapping,
    addStatementDraftMapping,
    setStatementDraftMappingAmount,
    confirmStatementDraftMapping,
    editStatementDraftMapping,
    removeStatementDraftMapping,
  },
  reducer: addMappingModalReducer,
} = slice;
